import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as tripPackageInventorySlice from '../../../data/tripPackageInventory.slice';
import * as referenceSlice from '../../../data/reference.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import { TripLegAddress } from '../../../data/models/trip-leg.model';
import TripPackageRejectDialog from './TripPackageRejectDialog';
import { TransportationAttribute } from '../../../data/models/transportation-attribute.model';
import dateUtils from '../../../services/DateUtils';
import TripPackageSummary from './TripPackageSummary';
import { financialUtils } from '../../../services/financialUtils';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import { PackageTripLegItem } from '../../../data/models/trip-leg-package.model';

const PREFIX = 'TripPackageDialog';

const classes = {
  uploadButton: `${PREFIX}-uploadButton`,
  form: `${PREFIX}-form`,
  control: `${PREFIX}-control`,
  grid: `${PREFIX}-grid`,
  address: `${PREFIX}-address`,
  line2: `${PREFIX}-line2`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.uploadButton}`]: {
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.form}`]: {
    margin: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.control}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.grid}`]: {
    marginTop: theme.spacing(2),
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },

  [`& .${classes.address}`]: {
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.line2}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    fontWeight: 400,
    fontSize: '0.675rem',
  },
}));

interface TripPackageDialogProps {
  open: boolean;
  packageUUID: string;
  close: () => void;
}

export default function TripPackageDialog({
  open,
  packageUUID,
  close,
}: TripPackageDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const providerID = useLoggedInProviderId();
  const timezones = useAppSelector(referenceSlice.selectTimeZones);
  const tripModes = useAppSelector(referenceSlice.selectTripModes);

  const rejectReasons = useAppSelector(
    referenceSlice.selectTripLegPackageRejectReasons,
  );

  const [tripPackageRejectOpen, setTripPackageRejectOpen] =
    React.useState(false);

  const loadingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectLoadingTripLegPackage,
  );
  const tripPackage = useAppSelector(
    tripPackageInventorySlice.selectTripLegPackage,
  );
  const acceptingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectAcceptingTripPackage,
  );
  const rejectingTripPackage = useAppSelector(
    tripPackageInventorySlice.selectRejectingTripPackage,
  );
  const transportationAttributes = useAppSelector(
    accountingSlice.selectAllTransportationAttributes,
  );
  const offeredTripLegEvent = useAppSelector(
    tripPackageInventorySlice.selectOfferedTripLegEvent,
  );

  React.useEffect(() => {
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
    dispatch(referenceSlice.fetchTripModesAsync());
  }, []);

  React.useEffect(() => {
    if (providerID && packageUUID) {
      dispatch(
        tripPackageInventorySlice.getTripLegPackageAsync(
          providerID,
          packageUUID,
        ),
      );
    }
  }, [providerID, packageUUID]);

  React.useEffect(() => {
    if (offeredTripLegEvent && providerID && packageUUID) {
      dispatch(
        tripPackageInventorySlice.getTripLegPackageAsync(
          providerID,
          packageUUID,
        ),
      );
      dispatch(tripPackageInventorySlice.setOfferedTripLegEvent(false));
    }
  }, [offeredTripLegEvent, providerID, packageUUID, dispatch]);

  const acceptTripPackage = () => {
    dispatch(tripPackageInventorySlice.acceptTripPackageAsync(packageUUID));
    close();
  };

  const rejectTripPackage = () => {
    setTripPackageRejectOpen(true);
  };

  const closeDialog = () => {
    close();
  };

  const renderPickupTime = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    const isWillCall = params.row.isWillCall;

    const tz = dateUtils.getTimeZoneIana(
      timezones,
      isWillCall
        ? params.row.dropOffAddressTimeZoneID
        : params.row.pickUpAddressTimeZoneID,
    );

    return isWillCall ? (
      <Box>
        <Typography>
          {dateUtils.formatAppointmentDate(params.row.appointmentDate, tz)}{' '}
          {t('trips.willCall')}
        </Typography>
        <Typography className={classes.line2}>
          {params.row.tripLegReferenceID}
        </Typography>
      </Box>
    ) : (
      <Box>
        <Typography>
          {dateUtils.formatPickupDateTime(params.row.pickUpDueTimeUTC, tz)}
        </Typography>
        <Typography className={classes.line2}>
          {params.row.tripLegReferenceID}
        </Typography>
      </Box>
    );
  };

  const renderAddress = (address: TripLegAddress) => {
    return (
      <Box className={classes.address}>
        <Typography>{`${address.addressLine1}`}</Typography>
        <Typography
          className={
            classes.line2
          }>{`${address.city}, ${address.stateCode} ${address.zipCode}`}</Typography>
      </Box>
    );
  };

  const renderPassenger = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    const passenger = params.row.reservation.passenger;
    const name = passenger
      ? `${passenger.passengerFirstName ?? ''} ${
          passenger.passengerLastName ?? ''
        }`
      : '';
    const count = params.row.companionCount + 1;
    const key =
      count === 1
        ? 'notification.passengerCount'
        : 'notification.passengersCount';
    return (
      <Box>
        <Typography>{name}</Typography>
        <Typography className={classes.line2}>{t(key, { count })}</Typography>
      </Box>
    );
  };

  const renderPickupAddress = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    return renderAddress({
      addressLine1: params.row.pickUpAddressLine1,
      addressLine2: params.row.pickUpAddressLine2,
      city: params.row.pickUpCity,
      stateCode: params.row.pickUpStateCode,
      zipCode: params.row.pickUpZipCode,
    });
  };

  const renderDropoffAddress = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    return renderAddress({
      addressLine1: params.row.dropOffAddressLine1,
      addressLine2: params.row.dropOffAddressLine2,
      city: params.row.dropOffCity,
      stateCode: params.row.dropOffStateCode,
      zipCode: params.row.dropOffZipCode,
    });
  };

  const getAttributeByUUID = (uuid: string) => {
    return transportationAttributes.find(
      (a) => a.transportationAttributeUUID === uuid,
    );
  };

  const renderGurneyAttributes = (
    gurney: any,
    tripModes: TransportationAttribute[],
  ): React.ReactElement => {
    const header = getAttributeByUUID(
      gurney.gurneyTypeTransportationAttributeUUID,
    );
    const feet = Math.floor(gurney.memberHeightInches / 12);
    const inches =
      gurney.memberHeightInches % 12 === 0
        ? ''
        : gurney.memberHeightInches % 12;

    const oxygen = getAttributeByUUID(
      gurney.oxygenCapacityTransportationAttributeUUID,
    );
    const stairs = getAttributeByUUID(
      gurney.stairsCountTransportationAttributeUUID,
    );
    return (
      <Box>
        <Typography>
          {`${header?.name}` +
            `${gurney.waitAndReturn ? ' - Wait and Return' : ''}, ${tripModes
              .map((a) => {
                if (/\((\w+)\)/.test(a.name)) {
                  return a.name.replace(/.*\((\w+)\).*/, '$1');
                }
                return a.passengerCareLevelBC ?? a.name;
              })
              .join(', ')}`}
        </Typography>
        <Typography className={classes.line2}>
          {feet}' {inches}" {gurney.memberWeightLbs}lbs
          {stairs ? `, Stairs: ${stairs.name}` : ''}
          {oxygen && oxygen.name !== 'No' ? `, Oxygen: ${oxygen.name}` : ''}
        </Typography>
      </Box>
    );
  };

  // NOTE: DO NOT DELETE, look at method below renderTransportationAttributes
  // const renderTransportationAttributes = (
  //   params: GridCellParams<any, PackageTripLegItem>,
  // ): React.ReactElement => {
  //   const [top, rest]: [TransportationAttribute[], TransportationAttribute[]] =
  //     params.row.transportationAttributes.map((uuids: string[]) =>
  //       uuids.map(getAttributeByUUID),
  //     );
  //
  //   const tripModes = top
  //     .filter(
  //       (attr): attr is TransportationAttribute =>
  //         attr?.transportationAttributeCategoryBC !== 'AMBULATORY',
  //     )
  //     .concat(
  //       rest.filter(
  //         (attr) => attr.transportationAttributeCategoryBC === 'MOBILITY_AID',
  //       ),
  //     );
  //
  //   // if this is a gurney trip, we need a vastly different output.
  //   if (params.row.gurney) {
  //     const [, ...modes] = tripModes.reverse();
  //     return renderGurneyAttributes(params.row.gurney, modes);
  //   }
  //
  //   const careLevels = rest.filter(
  //     (attr): attr is TransportationAttribute =>
  //       attr?.transportationAttributeCategoryBC !== 'TRANSPORTMOBILITY' &&
  //       attr?.transportationAttributeCategoryBC !== 'AMBULATORY' &&
  //       attr?.transportationAttributeCategoryBC !== 'MOBILITY_AID',
  //   );
  //   return (
  //     <Box>
  //       <Typography>
  //         {tripModes
  //           .reverse()
  //           .map((a) => {
  //             if (/\((\w+)\)/.test(a.name)) {
  //               return a.name.replace(/.*\((\w+)\).*/, '$1');
  //             }
  //             return a.passengerCareLevelBC ?? a.name;
  //           })
  //           .join(', ')}
  //       </Typography>
  //       <Typography className={classes.line2}>
  //         {careLevels.map((a) => a.name).join(', ')}
  //       </Typography>
  //     </Box>
  //   );
  // };

  const renderTransportationAttributes = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    const tripMode = tripModes.find(
      (mode) => mode.tripModeID === params.row.tripModeID,
    )!;

    let text = `${tripMode.tripModeName}, ${params.row.passengerCareLevelBC}`;
    if (tripMode.tripModeBC === 'AMBI') {
      text = params.row.passengerCareLevelBC as string;
    }

    // TODO: once backend supports gurney attributes, finish implementation. Related ticket RC-3192 & RC-3193
    // TODO: old implementation commented above

    return <Typography>{text}</Typography>;
  };

  const renderDistance = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    return <>{params.row.estimatedDistanceMiles || ''}</>;
  };

  const renderPrice = (
    params: GridCellParams<any, PackageTripLegItem>,
  ): React.ReactElement => {
    const payableAmount =
      params.row.tripLegServicePrice?.payableAmount ?? params.row.payableAmount;
    return (
      <>{payableAmount ? financialUtils.formatPrice(payableAmount) : ''}</>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: t('notification.pickupTime') || '',
      width: 180,
      sortable: false,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('notification.pickupAddress') || '',
      flex: 1,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('notification.dropoffAddress') || '',
      flex: 1,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'passenger',
      headerName: t('notification.passenger') || '',
      flex: 1,
      sortable: false,
      renderCell: renderPassenger,
    },
    {
      field: 'attributes',
      headerName: t('notification.serviceLevel') || '',
      flex: 1,
      sortable: false,
      renderCell: renderTransportationAttributes,
    },
    {
      field: 'distance',
      headerName: t('notification.distance') || '',
      width: 80,
      sortable: false,
      renderCell: renderDistance,
    },
    {
      field: 'price',
      headerName: t('notification.price') || '',
      width: 80,
      sortable: false,
      renderCell: renderPrice,
    },
  ];

  const closeTripPackageReject = (reason?: string) => {
    if (reason) {
      dispatch(
        tripPackageInventorySlice.rejectTripPackageAsync(packageUUID, reason),
      );
    }
    setTripPackageRejectOpen(false);
    close();
  };

  useEffect(() => {
    dispatch(referenceSlice.fetchTimeZonesAsync());
  }, []);

  return (
    <StyledDialog
      open={open}
      maxWidth="xl"
      fullWidth={true}
      scroll="body"
      onClose={closeDialog}>
      <DialogTitle>{t('notification.tripPackageTitle')}</DialogTitle>
      <DialogContent>
        {/* {t('notification.tripPackageMessage')} */}
        {tripPackage && <TripPackageSummary tripPackage={tripPackage} />}
        <div>
          <DataGrid
            getRowId={(row) => row.tripLegReferenceID}
            loading={loadingTripPackage}
            rows={tripPackage?.tripLegs || []}
            columns={columns}
            autoHeight={true}
            className={classes.grid}
            checkboxSelection={false}
            disableColumnMenu={true}
            disableColumnSelector={true}
            hideFooter={true}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          onClick={acceptTripPackage}
          disabled={acceptingTripPackage || rejectingTripPackage}
          data-fs-element="TripPackageAcceptButton">
          {t('notification.accept')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={rejectTripPackage}
          disabled={acceptingTripPackage || rejectingTripPackage}
          data-fs-element="TripPackageDenyButton">
          {t('notification.decline')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => closeDialog()}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
      <TripPackageRejectDialog
        rejectReasons={rejectReasons}
        open={tripPackageRejectOpen}
        close={closeTripPackageReject}
      />
    </StyledDialog>
  );
}
